// Engine Room Log -> Equipment hours sync.
// Rules:
//  1. Hours only ever go UP. A lower reading is a typo -> flag, never write.
//  2. For a given date, the row with the highest Baserow id wins (API order is not guaranteed).
//  3. Equipment rows are created on first reading, so a new machine on the
//     checks form registers itself. Vessel-agnostic: no table ids here.

// Engine Log field name -> canonical Equipment name
export const HOURS_MAP = {
  'ME Port Hours':  'Main Engine (Port)',
  'ME Stbd Hours':  'Main Engine (Stbd)',
  'Gen Port Hours': 'Generator (Port)',
  'Gen Stbd Hours': 'Generator (Stbd)',
}

// Baserow Equipment PRIMARY field is 'UID'. Link fields (Maintenance Log ->
// Component) resolve on the primary field only, so every auto-created row needs one.
export const EQUIP_UID = {
  'Main Engine (Port)':  'EQ-ME-P',
  'Main Engine (Stbd)':  'EQ-ME-S',
  'Generator (Port)':    'EQ-GEN-P',
  'Generator (Stbd)':    'EQ-GEN-S',
}

export const EQUIP_DEFAULTS = {
  'Main Engine (Port)':  { Department: 'Engineering', System: 'Propulsion',       Location: 'Engine Room' },
  'Main Engine (Stbd)':  { Department: 'Engineering', System: 'Propulsion',       Location: 'Engine Room' },
  'Generator (Port)':    { Department: 'Engineering', System: 'Power Generation',  Location: 'Engine Room' },
  'Generator (Stbd)':    { Department: 'Engineering', System: 'Power Generation',  Location: 'Engine Room' },
}

const num = v => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(String(v).replace(/[^0-9.\-]/g, ''))
  return Number.isFinite(n) ? n : null
}

export const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '')

// From a set of log rows, pick the authoritative reading per machine.
// Latest DATE wins; ties on date broken by highest id.
export function latestReadings(logRows) {
  const out = {}
  for (const row of logRows || []) {
    const date = String(row['Date'] || '')
    const id = Number(row.id) || 0
    for (const [field, name] of Object.entries(HOURS_MAP)) {
      const h = num(row[field])
      if (h === null) continue
      const prev = out[name]
      if (!prev || date > prev.date || (date === prev.date && id > prev.id)) {
        out[name] = { name, hours: h, date, id }
      }
    }
  }
  return out
}

// Diff readings against Equipment rows. Never lowers hours.
export function planSync(readings, equipRows) {
  const byName = {}
  for (const e of equipRows || []) byName[norm(e['Name'])] = e

  const creates = [], updates = [], flags = []
  for (const r of Object.values(readings)) {
    const match = byName[norm(r.name)]
    if (!match) { creates.push(r); continue }
    const cur = num(match['Current Hours'])
    if (cur === null || r.hours > cur) {
      updates.push({ ...r, rowId: match.id, from: cur })
    } else if (r.hours < cur) {
      flags.push({ ...r, rowId: match.id, current: cur,
        msg: `${r.name}: log reads ${r.hours}h but Equipment holds ${cur}h. Hours cannot go backwards — reading not applied.` })
    }
  }
  return { creates, updates, flags }
}

// ---- Maintenance Log / Task derivation from an ER log entry ----

const slug = s => String(s || '').toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '')

// Equipment-hours table row index -> canonical Equipment name
export const EQ_ROW_NAMES = [
  'Main Engine (Port)', 'Main Engine (Stbd)',
  'Generator (Port)', 'Generator (Stbd)',
]

// Build the Maintenance Log rows a single ER log entry should produce.
// 1 routine row for the check itself + 1 servicing row per machine with oil added.
export function maintenanceRowsFor(header, readings) {
  const date = header?.date || ''
  const tech = header?.signed_by || ''
  if (!date) return []

  // 'Component' is a link-to-Equipment field in Baserow \u2014 it only accepts
  // values matching an Equipment row name. The routine row has no single machine,
  // so the field is omitted entirely rather than sent as free text.
  const rows = [{
    'Log ID': `ERL-${date}`,
    'Task': 'Daily engine room checks',
    'Work Description': 'Routine daily engine room round: tank levels, generator readings, equipment hours, batteries, bilges.',
    'Date Completed': date,
    'Technician': tech,
  }]

  const eq = (readings?.equipment) || []
  eq.forEach((row, i) => {
    const name = EQ_ROW_NAMES[i] || row?.name
    const oil = String(row?.oil_added || '').trim()
    if (!name || !oil) return
    const hrs = num(row?.hours)
    const r = {
      'Log ID': `OIL-${date}-${slug(name)}`,
      'Component': EQUIP_UID[name] || name,
      'Task': `Oil added \u2014 ${name}`,
      'Work Description': `Oil added during daily checks: ${oil}`,
      'Date Completed': date,
      'Technician': tech,
      'Parts Used': 'Lube oil',
    }
    if (hrs !== null) r['Hours at Service'] = hrs
    rows.push(r)
  })

  return rows
}

// Machines whose hours have reached the Next Service figure entered on the form.
export function serviceDue(readings) {
  const out = []
  const eq = (readings?.equipment) || []
  eq.forEach((row, i) => {
    const name = EQ_ROW_NAMES[i] || row?.name
    const hrs = num(row?.hours)
    const next = num(row?.next_service)
    if (!name || hrs === null || next === null || next <= 0) return
    if (hrs >= next) out.push({ name, hours: hrs, dueAt: next })
  })
  return out
}
